import type { CaptureContext } from '@sentry/core';

export type LoggerLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

/**
 * Options shared across logger methods for consistent Sentry payloads.
 */
export interface LoggerReportOptions {
	/** Logical feature area, mapped to a Sentry `feature` tag. */
	feature?: string;
	/** Additional Sentry tags. */
	tags?: Record<string, string>;
	/** Arbitrary diagnostic data attached to the Sentry event. */
	extra?: Record<string, unknown>;
	/** Prefix for console output, e.g. component or service name. */
	source?: string;
	/** Sentry event severity when capturing a message. */
	level?: LoggerLevel;
}

export function toError(value: string | Error): Error {
	return value instanceof Error ? value : new Error(value);
}

export function formatConsolePrefix(source?: string): string {
	return source ? `[${source}]` : '[App]';
}

/**
 * Builds a Sentry {@link CaptureContext} from app-level logger options.
 */
export function toCaptureContext(
	options?: LoggerReportOptions,
): CaptureContext | undefined {
	if (!options) return undefined;

	const { feature, tags, extra, level } = options;
	const captureTags: Record<string, string> = { ...tags };

	if (feature) captureTags['feature'] = feature;

	const hasTags = Object.keys(captureTags).length > 0;
	const hasExtra = extra && Object.keys(extra).length > 0;

	if (!hasTags && !hasExtra && !level) return undefined;

	return {
		...(hasTags ? { tags: captureTags } : {}),
		...(hasExtra ? { extra } : {}),
		...(level ? { level } : {}),
	};
}
